<p>This is where you create <em>new</em> PDF versions of your books. If you're looking for the PDFs that you have already created, <a [href]="project?.publicationUrl" target="_blank">click here</a>.</p>

@if( project ) {
<mat-stepper orientation="vertical" [linear]="true" #stepper>
  <!--  (selectionChange)="onStepChange($event)" -->
    <mat-step>
      <ng-template matStepLabel>Confirm the publication settings</ng-template>

      <app-publication-configuration-selector [project]="project" (configurationSelected)="onConfigurationSelected($event)"></app-publication-configuration-selector>


      @if( publicationConfiguration ) {
        @if( publicationConfiguration.canonsWithParsingFormats.length == 0 ) {
          <p class="warning">You won't be able to publish with this configuration because you haven't selected a parsing format for any configuration. Go to <a [routerLink]="['/settings', project.id]">Project Settings</a> to fix this. See <a href="https://docs.openreadersbibles.org/preparing-to-publish/" _target="blank">Preparing to Publish</a> in the documentation for more information.</p>
        } @else if( publicationConfiguration.canonsWithoutParsingFormats.length > 0 ) {
          <p class="warning">You will be able to publish books from canon(s): {{canonsWithParsingFormats}}. But you cannot publish books from canon(s) {{canonsWithoutParsingFormats}} until you select a parsing format for this configuration. Go to <a [routerLink]="['/settings', project.id]">Project Settings</a> to fix this. See <a href="https://docs.openreadersbibles.org/preparing-to-publish/" _target="blank">Preparing to Publish</a> in the documentation for more information.</p>
        }

        @if( possibleToPublishWithCurrentPublicationConfiguration ) {
          <p>If any of these don't look right, you can change them in the <a [routerLink]="['/settings', project.id]">Project Settings</a>.</p>
          <table class="settings">
            <tr>
              <th>Project Language Font</th>
              <td>{{publicationConfiguration.publicationProjectFont}}</td>
            </tr>
            <tr>
              <th>Biblical Language Font</th>
              <td>{{publicationConfiguration.publicationBiblicalFont}}</td>
            </tr>
            <tr>
              <th>Typography Language</th>
              <td>{{publicationConfiguration.polyglossiaOtherLanguage}}</td>
            </tr>
            <tr>
              <th>Numbers</th>
              <td>{{demoChangeNumerals("1234567890")}}</td>
            </tr>
            <tr>
              <th>Sample Chapter Header</th>
              <td>{{publicationConfiguration.demoChapterHeader("15")}}</td>
            </tr>
            <tr>
              <th>First ten footnotes</th>
              <td>{{publicationConfiguration.demoFootnoteMarkers(10)}}</td>
            </tr>
          </table>
        }
      }

      <div>
        <button mat-button matStepperNext [disabled]="!possibleToPublishWithCurrentPublicationConfiguration">Next</button>
      </div>
    </mat-step>

    <mat-step [stepControl]="chooseBooksFormGroup">
      <form [formGroup]="chooseBooksFormGroup">
      <ng-template matStepLabel>Choose books</ng-template>
      <p>Select the books you want to include in the publication.</p> 
      <p>To avoid running into server limitations, please limit yourself to publishing up to 150 total chapters at a time.</p>
      <p [ngClass]=" numberOfChaptersSelected > 150 ? 'warning' : 'fine' ">Number of chapters currently selected: {{numberOfChaptersSelected}}</p>

      @for (c of publicationConfiguration?.canonsWithParsingFormats; track c) {
        @if(showPublishButton) {
        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>{{c}}</mat-panel-title>
          </mat-expansion-panel-header>
          <mat-selection-list (selectionChange)="booksSelected($event)" [id]="c">
              @for (book of canonData(c).books; track book; let i = $index) {
                <mat-list-option [value]="c + ' ' + book">{{project.getBookName(book)}}</mat-list-option>
              }
          </mat-selection-list>
          </mat-expansion-panel>
        }
      }

      <div class="select-all-none">
        <button mat-button type="button" (click)="selectAllBooks()">Select All</button>
        <button mat-button type="button" (click)="selectNoBooks()">Select None</button>
      </div>
      
      @if(!atLeastOneSelected) {
        <p style="color: red;">At least one book must be selected.</p>
      }

      <div>
        <button mat-button matStepperPrevious>Back</button>
        <button mat-button matStepperNext type="button" [disabled]="!atLeastOneSelected" (click)="makeApiCalls()">Next</button>
      </div>
      </form>
    </mat-step>

    <mat-step>
      <ng-template matStepLabel>Check books</ng-template>
      <p>These checks will alert you to any missing glosses in your books. You can still go ahead with publication if you wish, but you should be aware that there will be gaps where there are no glosses.</p>
      @for( book of selectedBooks; track book) {
        @if( apiResults[book.toString()] ) {
          @switch (apiResults[book.toString()].status) {  
            @case ('loading') {
              <div>
                <mat-progress-spinner diameter="20"></mat-progress-spinner> Checking {{ book }}...
              </div>
              }
            @case ('success') {
              <div class="results-div">
                <mat-icon style="color: green;">check_circle</mat-icon> <span style="color: green;">Success: {{ localizedNameFromBookId(book) }}</span> 
              </div>                
            }
            @case ('failure') {
              <div class="results-div">
                <mat-icon style="color: red;">error</mat-icon> <span style="color: red;">Failure: {{ localizedNameFromBookId(book) }}: {{ apiResults[book.toString()].message }}</span> 
              </div>
              }
          }
        }
      }
      <button mat-button matStepperPrevious>Back</button>
      <button mat-button matStepperNext>Next</button>
  </mat-step>

    <mat-step>

      <ng-template matStepLabel>Publish</ng-template>

      @if( showPublishButton ) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>Advanced options</mat-panel-title>
        </mat-expansion-panel-header>
        <form [formGroup]="advancedOptionsFormGroup">
          <mat-checkbox formControlName="nopdf">Do not generate PDFs (only JSON, XML, and TeX)</mat-checkbox>
        </form>
      </mat-expansion-panel>

      <div>
        <p>You're all set! Click the button below to publish your books!</p>
        <button mat-button (click)="publish()" [disabled]="disablePublishButton">
          @if( disablePublishButton ) { <mat-icon class="spinner">autorenew</mat-icon> }
          {{ disablePublishButton ? 'Publishing...' : 'Publish' }}
          </button>  
      </div>
      }

      <app-publication-status [commitSha]="commitSha" [project]="project"></app-publication-status>

      @if( showFallbackPublicationResult ) {
      <div class="status-container">
          <p>The server responded in a way we didn't expect. This is our problem, not yours!</p>
      </div>
      }
      
      @if( showTimeoutPublicationResult ) {
        <div class="status-container">
            <p>The job took too long to track here. Perhaps it was a large book, or just a large number of books. This is not necessarily a problem. You can check your project's  <a [href]="project.publicationUrl" target="_blank">distribution page</a> in a little bit (perhaps 20-30 minutes?) to see if the books have been published.</p>
        </div>
        }
        
    </mat-step>
  </mat-stepper>
}